import { FunctionComponent, useState } from "react";
import { useAppSelector, useAppDispatch } from "./store/hooks";
import { createPayment } from "./services/PaymentService";
import { updateProfileField } from "./store/userSlice";
import { addToast } from "./store/toastSlice";
import { getAuth } from "firebase/auth";

async function authHeader(): Promise<Record<string, string>> {
  const u = getAuth().currentUser;
  if (!u) return {};
  const token = await u.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

type WalletPanelProps = { onClose: () => void };
type Tab = "deposit" | "withdraw" | "crypto";

const CRYPTO_NETWORKS = [
  { currency: "USDT", network: "TRC20", label: "USDT (TRC-20)" },
  { currency: "USDT", network: "ERC20", label: "USDT (ERC-20)" },
  { currency: "BTC",  network: "BTC",   label: "Bitcoin (BTC)" },
  { currency: "ETH",  network: "ETH",   label: "Ethereum (ETH)" },
  { currency: "TON",  network: "TON",   label: "TON" },
];

const PRESETS = [
  { rub: 100,  label: "100 ₽"  },
  { rub: 250,  label: "250 ₽"  },
  { rub: 500,  label: "500 ₽"  },
  { rub: 1000, label: "1 000 ₽" },
  { rub: 2500, label: "2 500 ₽" },
];

const MIN_WITHDRAW = 500;

const WalletPanel: FunctionComponent<WalletPanelProps> = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const profile  = useAppSelector((s) => s.user.profile);

  const [tab, setTab]               = useState<Tab>("deposit");

  // Крипто
  const [cryptoMode, setCryptoMode]   = useState<"deposit"|"withdraw">("deposit");
  const [cryptoNet, setCryptoNet]     = useState(CRYPTO_NETWORKS[0]);
  const [cryptoAmt, setCryptoAmt]     = useState("");
  const [cryptoAddr, setCryptoAddr]   = useState("");
  const [cryptoLoading, setCryptoLoading] = useState(false);

  // Пополнение
  const [selectedIdx, setSelectedIdx] = useState(2);
  const [customAmt, setCustomAmt]     = useState("");
  const [useCustom, setUseCustom]     = useState(false);
  const [depLoading, setDepLoading]   = useState(false);

  // Вывод
  const [wdAmount, setWdAmount]       = useState("");
  const [wdPhone, setWdPhone]         = useState("");
  const [wdLoading, setWdLoading]     = useState(false);

  if (!profile) return null;

  // ── Пополнение ───────────────────────────────────────────
  const preset    = PRESETS[selectedIdx];
  const depAmount = useCustom ? parseInt(customAmt || "0", 10) : preset.rub;
  const canDeposit = depAmount >= 50 && depAmount <= 100_000;

  const handleDeposit = async () => {
    if (!canDeposit || depLoading) return;
    setDepLoading(true);

    // iOS Safari/PWA — открываем окно СИНХРОННО до await
    const payWin = window.open("", "_blank");

    try {
      const { payment_url } = await createPayment(depAmount, profile.uid);
      if (payWin) {
        payWin.location.href = payment_url;
      } else {
        window.location.href = payment_url;
      }
      dispatch(addToast({ type: "info", message: "Открываем страницу оплаты...", subtext: "После оплаты монеты поступят автоматически" }));
      onClose();
    } catch (err: any) {
      if (payWin) payWin.close();
      dispatch(addToast({ type: "error", message: "Ошибка платежа", subtext: err.message }));
    } finally {
      setDepLoading(false);
    }
  };

  // ── Крипто ──────────────────────────────────────────────
  const cryptoAmtNum = parseInt(cryptoAmt || "0", 10);
  const canCryptoDeposit  = cryptoAmtNum >= 100;
  const canCryptoWithdraw = cryptoAmtNum >= MIN_WITHDRAW && cryptoAmtNum <= profile.coins && cryptoAddr.trim().length > 10;

  const handleCryptoDeposit = async () => {
    if (!canCryptoDeposit || cryptoLoading) return;
    setCryptoLoading(true);
    const payWin = window.open("", "_blank");
    try {
      const resp = await fetch("/payment/crypto/create", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: cryptoAmtNum, uid: profile.uid }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Ошибка");
      if (payWin) payWin.location.href = data.payment_url;
      else window.location.href = data.payment_url;
      dispatch(addToast({ type: "info", message: "Открываем крипто-оплату...", subtext: "После оплаты монеты поступят автоматически" }));
      onClose();
    } catch (err: any) {
      if (payWin) payWin.close();
      dispatch(addToast({ type: "error", message: "Ошибка", subtext: err.message }));
    } finally { setCryptoLoading(false); }
  };

  const handleCryptoWithdraw = async () => {
    if (!canCryptoWithdraw || cryptoLoading) return;
    setCryptoLoading(true);
    try {
      const headers = { "Content-Type": "application/json", ...(await authHeader()) };
      const resp = await fetch("/payment/crypto/withdraw", {
        method: "POST", headers,
        body: JSON.stringify({ amount: cryptoAmtNum, address: cryptoAddr.trim(), network: cryptoNet.network, currency: cryptoNet.currency }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Ошибка сервера");
      dispatch(updateProfileField({ coins: profile.coins - cryptoAmtNum }));
      dispatch(addToast({ type: "coins", message: `Заявка на вывод ${cryptoAmtNum} ₽ в крипто принята`, subtext: "Вывод в течение 24 часов" }));
      setCryptoAmt(""); setCryptoAddr("");
      onClose();
    } catch (err: any) {
      dispatch(addToast({ type: "error", message: "Ошибка", subtext: err.message }));
    } finally { setCryptoLoading(false); }
  };

  // ── Вывод ────────────────────────────────────────────────
  const wdAmountNum = parseInt(wdAmount || "0", 10);
  const canWithdraw = wdAmountNum >= MIN_WITHDRAW &&
                      wdAmountNum <= profile.coins &&
                      wdPhone.replace(/\D/g, "").length >= 10;

  const handleWithdraw = async () => {
    if (!canWithdraw || wdLoading) return;
    setWdLoading(true);
    try {
      const headers = { "Content-Type": "application/json", ...(await authHeader()) };
      const resp = await fetch("/payment/withdraw", {
        method:  "POST",
        headers,
        body:    JSON.stringify({ amount: wdAmountNum, phone: wdPhone }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Ошибка сервера");

      // Сразу обновляем баланс в Redux
      dispatch(updateProfileField({ coins: profile.coins - wdAmountNum }));
      dispatch(addToast({ type: "coins", message: `Заявка на вывод ${wdAmountNum} ₽ принята`, subtext: "Средства поступят в течение 24 часов" }));
      setWdAmount("");
      setWdPhone("");
      onClose();
    } catch (err: any) {
      dispatch(addToast({ type: "error", message: "Ошибка вывода", subtext: err.message }));
    } finally {
      setWdLoading(false);
    }
  };

  return (
    <div className="Overlay" onClick={onClose}>
      <div className="Modal Wallet-modal" onClick={(e) => e.stopPropagation()}>

        <div className="Modal-header">
          <div className="Modal-title">Баланс</div>
          <button className="Modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="Wallet-balance">
          <span className="Wallet-balance-label">Ваш баланс</span>
          <span className="Wallet-balance-value">● {profile.coins.toLocaleString("ru")} ₽</span>
        </div>

        {/* Вкладки */}
        <div className="Wallet-tabs">
          <button className={`Wallet-tab${tab === "deposit"  ? " active" : ""}`} onClick={() => setTab("deposit")}>Пополнить</button>
          <button className={`Wallet-tab${tab === "withdraw" ? " active" : ""}`} onClick={() => setTab("withdraw")}>Вывести</button>
          <button className={`Wallet-tab${tab === "crypto"   ? " active" : ""}`} onClick={() => setTab("crypto")}>₿ Крипто</button>
        </div>

        {/* ── Пополнение ── */}
        {tab === "deposit" && (
          <>
            <div className="Wallet-presets">
              {PRESETS.map((p, i) => (
                <button
                  key={p.rub}
                  className={`Wallet-preset-btn${selectedIdx === i && !useCustom ? " active" : ""}`}
                  onClick={() => { setSelectedIdx(i); setUseCustom(false); }}
                >{p.label}</button>
              ))}
            </div>

            <div className="Wallet-custom">
              <span className="Wallet-custom-label">Своя сумма:</span>
              <div className="Wallet-custom-row">
                <input
                  className={`Wallet-custom-input${useCustom ? " active" : ""}`}
                  type="number" inputMode="numeric"
                  placeholder="Сумма ₽" min={50} max={100000}
                  value={customAmt}
                  onChange={(e) => { setCustomAmt(e.target.value); setUseCustom(true); }}
                  onFocus={() => setUseCustom(true)}
                />
              </div>
            </div>

            {canDeposit && (
              <div className="Wallet-summary">
                <span>Будет зачислено:</span>
                <strong>● {depAmount.toLocaleString("ru")} монет</strong>
              </div>
            )}
            {useCustom && !canDeposit && (
              <div className="Wallet-error">Минимальная сумма — 50 ₽</div>
            )}

            <button
              className="Wallet-pay-btn"
              disabled={!canDeposit || depLoading}
              onClick={handleDeposit}
            >
              {depLoading ? <span className="Btn-spinner" /> : `Оплатить ${canDeposit ? depAmount.toLocaleString("ru") + " ₽" : ""}`}
            </button>
            <div className="Wallet-hint">Оплата через СБП / карту · Зачисление мгновенно</div>
          </>
        )}

        {/* ── Вывод ── */}
        {tab === "withdraw" && (
          <>
            <div className="Wallet-withdraw-info">
              Минимальная сумма вывода — <strong>{MIN_WITHDRAW} ₽</strong>.<br/>
              Средства поступают на счёт в течение 24 часов.
            </div>

            <div className="Wallet-custom">
              <span className="Wallet-custom-label">Сумма вывода:</span>
              <div className="Wallet-custom-row">
                <input
                  className="Wallet-custom-input"
                  type="number" inputMode="numeric"
                  placeholder={`От ${MIN_WITHDRAW} ₽`}
                  min={MIN_WITHDRAW} max={profile.coins}
                  value={wdAmount}
                  onChange={(e) => setWdAmount(e.target.value)}
                />
              </div>
            </div>

            <div className="Wallet-custom">
              <span className="Wallet-custom-label">Номер телефона (СБП):</span>
              <div className="Wallet-custom-row">
                <input
                  className="Wallet-custom-input"
                  type="tel" inputMode="tel"
                  placeholder="+7 900 000-00-00"
                  value={wdPhone}
                  onChange={(e) => setWdPhone(e.target.value)}
                />
              </div>
            </div>

            {wdAmountNum > profile.coins && (
              <div className="Wallet-error">Недостаточно средств на балансе</div>
            )}
            {wdAmountNum > 0 && wdAmountNum < MIN_WITHDRAW && (
              <div className="Wallet-error">Минимальная сумма — {MIN_WITHDRAW} ₽</div>
            )}

            <button
              className="Wallet-pay-btn"
              disabled={!canWithdraw || wdLoading}
              onClick={handleWithdraw}
            >
              {wdLoading ? <span className="Btn-spinner" /> : `Вывести${wdAmountNum >= MIN_WITHDRAW ? " " + wdAmountNum.toLocaleString("ru") + " ₽" : ""}`}
            </button>
            <div className="Wallet-hint">Вывод на номер телефона через СБП</div>
          </>
        )}

        {/* ── Крипто ── */}
        {tab === "crypto" && (
          <>
            {/* Режим: пополнение / вывод */}
            <div className="Wallet-tabs" style={{marginBottom:0}}>
              <button className={`Wallet-tab${cryptoMode === "deposit"  ? " active" : ""}`} onClick={() => setCryptoMode("deposit")}>Пополнить</button>
              <button className={`Wallet-tab${cryptoMode === "withdraw" ? " active" : ""}`} onClick={() => setCryptoMode("withdraw")}>Вывести</button>
            </div>

            {/* Сеть */}
            <div className="Wallet-custom">
              <span className="Wallet-custom-label">Криптовалюта и сеть</span>
              <div className="Wallet-custom-row">
                <select
                  className="Wallet-custom-input"
                  value={`${cryptoNet.currency}_${cryptoNet.network}`}
                  onChange={e => setCryptoNet(CRYPTO_NETWORKS.find(n => `${n.currency}_${n.network}` === e.target.value) || CRYPTO_NETWORKS[0])}
                >
                  {CRYPTO_NETWORKS.map(n => <option key={`${n.currency}_${n.network}`} value={`${n.currency}_${n.network}`}>{n.label}</option>)}
                </select>
              </div>
            </div>

            {/* Сумма */}
            <div className="Wallet-custom">
              <span className="Wallet-custom-label">Сумма в рублях {cryptoMode === "deposit" ? "(мин. 100 ₽)" : `(мин. ${MIN_WITHDRAW} ₽)`}</span>
              <div className="Wallet-custom-row">
                <input className="Wallet-custom-input" type="number" inputMode="numeric"
                  placeholder="Сумма ₽" value={cryptoAmt} onChange={e => setCryptoAmt(e.target.value)} />
              </div>
            </div>

            {/* Адрес — только для вывода */}
            {cryptoMode === "withdraw" && (
              <div className="Wallet-custom">
                <span className="Wallet-custom-label">Адрес кошелька</span>
                <div className="Wallet-custom-row">
                  <input className="Wallet-custom-input" type="text"
                    placeholder="0x... или T..." value={cryptoAddr} onChange={e => setCryptoAddr(e.target.value)} />
                </div>
              </div>
            )}

            {cryptoMode === "deposit" && (
              <div className="Wallet-withdraw-info">
                Вы оплатите в криптовалюте — сумма конвертируется по текущему курсу.<br/>
                Монеты поступят после подтверждения транзакции.
              </div>
            )}

            {cryptoMode === "withdraw" && cryptoAmtNum > profile.coins && (
              <div className="Wallet-error">Недостаточно средств</div>
            )}

            <button
              className="Wallet-pay-btn"
              disabled={cryptoLoading || (cryptoMode === "deposit" ? !canCryptoDeposit : !canCryptoWithdraw)}
              onClick={cryptoMode === "deposit" ? handleCryptoDeposit : handleCryptoWithdraw}
            >
              {cryptoLoading ? <span className="Btn-spinner" /> : (
                cryptoMode === "deposit"
                  ? `Оплатить крипто${canCryptoDeposit ? " " + cryptoAmtNum.toLocaleString("ru") + " ₽" : ""}`
                  : `Вывести в ${cryptoNet.label}${canCryptoWithdraw ? " " + cryptoAmtNum.toLocaleString("ru") + " ₽" : ""}`
              )}
            </button>
            <div className="Wallet-hint">Обработка через Heleket · Зачисление после подтверждения в блокчейне</div>
          </>
        )}
      </div>
    </div>
  );
};

export default WalletPanel;
